import { AlertTriangle, Copy, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

type ErrorDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	error: string | Error | null;
	details?: string;
};

export function ErrorDialog({
	open,
	onOpenChange,
	title = 'Error',
	error,
	details,
}: ErrorDialogProps) {
	const [copied, setCopied] = useState(false);

	const errorMessage = error instanceof Error ? error.message : String(error || 'Unknown error');
	const errorStack = error instanceof Error ? error.stack : undefined;

	function handleCopy() {
		const fullError = [
			`Error: ${errorMessage}`,
			details ? `\nDetails: ${details}` : '',
			errorStack ? `\nStack:\n${errorStack}` : '',
		].join('');

		navigator.clipboard.writeText(fullError);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-destructive">
						<AlertTriangle className="size-5" />
						{title}
					</DialogTitle>
					<DialogDescription className="sr-only">Error details</DialogDescription>
				</DialogHeader>

				<div className="space-y-3">
					<div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
						<p className="text-sm font-medium text-destructive">{errorMessage}</p>
					</div>

					{details && (
						<div className="rounded-md border bg-muted p-3">
							<p className="text-xs text-muted-foreground mb-1">Details:</p>
							<p className="text-sm">{details}</p>
						</div>
					)}

					{errorStack && (
						<details className="group">
							<summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
								Stack trace
							</summary>
							<pre className="mt-2 overflow-auto rounded-md bg-muted p-3 text-xs max-h-40">
								{errorStack}
							</pre>
						</details>
					)}
				</div>

				<DialogFooter className="gap-2 sm:gap-0">
					<Button variant="outline" size="sm" onClick={handleCopy}>
						<Copy className="size-3 mr-1" />
						{copied ? 'Copied!' : 'Copy Error'}
					</Button>
					<Button variant="default" size="sm" onClick={() => onOpenChange(false)}>
						<X className="size-3 mr-1" />
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
